import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { BookOpen, Users, MessageCircle, PlayCircle, ArrowRight, Sparkles, Eye } from 'lucide-react';
import { LandingHeader } from '../components/landing/LandingHeader';
import { AuthModal } from '../components/landing/AuthModal';
import { ProductPreview } from '../components/landing/ProductPreview';
import { PricingFaq } from '../components/landing/PricingFaq';
import { SiteFooter } from '../components/landing/SiteFooter';

const COLLAB_IMG = 'https://images.unsplash.com/photo-1758525860435-502240649c59?auto=format&fit=crop&w=720&q=70';

export default function LandingPage() {
    const [authOpen, setAuthOpen] = useState(false);
    const [authMode, setAuthMode] = useState('login'); // login | register
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('signup') === '1') { setAuthMode('register'); setAuthOpen(true); }
    }, []);
    const navigate = useNavigate();
    const { guestLogin } = useAuth();
    const openAuth = (mode) => { setAuthMode(mode); setAuthOpen(true); };

    const handleGuestDemo = async () => {
        try {
            await guestLogin();
            navigate('/dashboard');
        } catch {
            toast.error('Could not start demo. Please try again.');
        }
    };

    return (
        <div className="min-h-screen bg-background" data-testid="landing-page">
            <LandingHeader onLogin={() => openAuth('login')} onSignup={() => openAuth('register')} onDemo={handleGuestDemo} />

            {/* ─── Hero Section ─── */}
            <section className="max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-12 md:pt-24 md:pb-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8 items-center">
                    <div className="md:col-span-7 lg:col-span-6 space-y-8 animate-fade-in">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-wide uppercase" style={{ fontFamily: "'Manrope', sans-serif" }}>
                            <Sparkles className="w-3.5 h-3.5" /> Now open for communities
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.1]" style={{ fontFamily: "'Fraunces', serif" }}>
                            Where small groups<br />
                            <span className="text-primary">learn together.</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-lg" style={{ fontFamily: "'Manrope', sans-serif" }}>
                            Host courses, spark discussions, and grow together in one beautiful space. Built for churches, study groups, and learning communities.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Button
                                onClick={() => openAuth('register')}
                                className="rounded-full h-12 px-8 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 [@media(hover:hover)]:hover:-translate-y-0.5 transition-all touch-manipulation shadow-sm"
                                data-testid="hero-get-started-btn"
                            >
                                Get started free <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                            <Button
                                onClick={handleGuestDemo}
                                variant="outline"
                                className="rounded-full h-12 px-8 text-base font-medium border-border hover:bg-muted/50 [@media(hover:hover)]:hover:-translate-y-0.5 transition-all touch-manipulation"
                                data-testid="hero-try-demo-btn"
                            >
                                <Eye className="w-4 h-4 mr-2" /> Try the demo
                            </Button>
                        </div>
                    </div>
                    <div className="md:col-span-5 lg:col-span-6 relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                        <ProductPreview />
                    </div>
                </div>
            </section>

            {/* ─── Features Bento Grid ─── */}
            <section id="features" className="scroll-mt-20 max-w-7xl mx-auto px-6 md:px-8 pt-12 pb-20 md:pt-16 md:pb-28">
                <div className="text-center mb-16 space-y-4 animate-fade-in">
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary" style={{ fontFamily: "'Manrope', sans-serif" }}>Features</p>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
                        Everything your group needs
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
                        From structured courses to live discussions, The Room gives your community the tools to learn and grow — together.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-6">
                    {/* Wide card — Courses */}
                    <div className="md:col-span-8 bg-card border border-border/50 rounded-2xl p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 overflow-hidden relative group" data-testid="feature-courses">
                        <div className="relative z-10">
                            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                                <BookOpen className="w-6 h-6 text-primary" />
                            </div>
                            <h3 className="text-xl sm:text-2xl font-bold mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Host Your Own Courses</h3>
                            <p className="text-muted-foreground max-w-md" style={{ fontFamily: "'Manrope', sans-serif" }}>
                                Create structured courses with lessons, resources, discussion questions, and video recordings. Track progress across your entire group.
                            </p>
                        </div>
                        <div className="absolute -bottom-4 -right-4 w-40 h-40 rounded-full bg-primary/5 group-hover:bg-primary/10 transition-colors duration-500" />
                    </div>
                    {/* Square card — Groups */}
                    <div className="md:col-span-4 bg-card border border-border/50 rounded-2xl p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 overflow-hidden relative" data-testid="feature-groups">
                        <div className="w-12 h-12 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-5">
                            <Users className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                        </div>
                        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Join Groups</h3>
                        <p className="text-sm text-muted-foreground" style={{ fontFamily: "'Manrope', sans-serif" }}>
                            Join with an invite code. Belong to multiple groups. One account, many communities.
                        </p>
                    </div>
                    {/* Tall card with image */}
                    <div className="md:col-span-4 md:row-span-2 rounded-2xl overflow-hidden border border-border/50 relative group" data-testid="feature-collaborate-img">
                        <img src={COLLAB_IMG} alt="People collaborating" loading="lazy" decoding="async" width="720" height="960" className="w-full h-full object-cover min-h-[280px] md:min-h-0" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/10" />
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                            <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Fraunces', serif" }}>Real Collaboration</h3>
                            <p className="text-sm text-white/90" style={{ fontFamily: "'Manrope', sans-serif" }}>Not just content delivery — real community.</p>
                        </div>
                    </div>
                    {/* Chat */}
                    <div className="md:col-span-4 bg-card border border-border/50 rounded-2xl p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid="feature-chat">
                        <div className="w-12 h-12 rounded-2xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-5">
                            <MessageCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Live Chat</h3>
                        <p className="text-sm text-muted-foreground" style={{ fontFamily: "'Manrope', sans-serif" }}>
                            Real-time group messaging that keeps conversations flowing between sessions.
                        </p>
                    </div>
                    {/* Video */}
                    <div className="md:col-span-4 bg-card border border-border/50 rounded-2xl p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid="feature-video">
                        <div className="w-12 h-12 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-5">
                            <PlayCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Video Meetings</h3>
                        <p className="text-sm text-muted-foreground" style={{ fontFamily: "'Manrope', sans-serif" }}>
                            Meet face-to-face with in-app video or Zoom recordings attached to every lesson.
                        </p>
                    </div>
                </div>
            </section>

            {/* ─── How It Works ─── */}
            <section id="how-it-works" className="scroll-mt-20 bg-muted/30 py-20 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <div className="text-center mb-16 space-y-4">
                        <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary" style={{ fontFamily: "'Manrope', sans-serif" }}>How It Works</p>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
                            Up and running in minutes
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
                        {[
                            { step: '01', title: 'Create your group', desc: 'Set up your community and get a unique invite code to share with members.', icon: Users },
                            { step: '02', title: 'Build your courses', desc: 'Add lessons, upload resources, set discussion questions, and attach video recordings.', icon: BookOpen },
                            { step: '03', title: 'Learn together', desc: 'Members join, engage with content, discuss in real-time, and track their progress.', icon: Sparkles },
                        ].map((item) => (
                            <div key={item.step} className="text-center space-y-4 group">
                                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto group-hover:-translate-y-1 transition-transform duration-300">
                                    <item.icon className="w-7 h-7 text-primary" />
                                </div>
                                <p className="text-xs font-bold text-primary tracking-widest" style={{ fontFamily: "'Manrope', sans-serif" }}>STEP {item.step}</p>
                                <h3 className="text-xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>{item.title}</h3>
                                <p className="text-sm text-muted-foreground max-w-xs mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <PricingFaq />

            {/* ─── CTA Section ─── */}
            <section className="max-w-7xl mx-auto px-6 md:px-8 pb-20 md:pb-32">
                <div className="bg-primary rounded-2xl p-8 md:p-16 text-center space-y-6">
                    <h2 className="text-3xl sm:text-4xl font-bold text-primary-foreground tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
                        Ready to bring your group together?
                    </h2>
                    <p className="text-primary-foreground/80 text-lg max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
                        Start hosting courses, building community, and tracking progress — all in one place. Free for one church group during early access.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                            onClick={() => openAuth('register')}
                            className="rounded-full h-12 px-8 text-base font-semibold bg-white text-primary hover:bg-white/90 [@media(hover:hover)]:hover:-translate-y-0.5 transition-all touch-manipulation"
                            data-testid="cta-get-started-btn"
                        >
                            Get started free <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                        <Button
                            onClick={handleGuestDemo}
                            variant="outline"
                            className="rounded-full h-12 px-8 text-base font-medium bg-transparent border-white/30 text-primary-foreground hover:bg-white/10 [@media(hover:hover)]:hover:-translate-y-0.5 transition-all touch-manipulation"
                            data-testid="cta-try-demo-btn"
                        >
                            <Eye className="w-4 h-4 mr-2" /> Try the demo
                        </Button>
                    </div>
                </div>
            </section>

            <SiteFooter />

            {/* ─── Auth Modal ─── */}
            <AuthModal
                open={authOpen}
                onClose={() => setAuthOpen(false)}
                mode={authMode}
                setMode={setAuthMode}
                onGuestDemo={handleGuestDemo}
            />
        </div>
    );
}
